import { dbShortDate, MDY_FORMAT } from 'utilities';
import {
  COMPANY_ROUTE_SLUGS,
  FIRM_ROUTE_SLUGS,
  getCompanyRouteTitleBySlug,
  getFirmRouteTitleBySlug,
  PAGE_NAMES,
  PATH_SEGMENTS,
  ROUTE_PATTERNS,
  ROUTE_TYPES,
  URL_PARAMS
} from './constants/titles';

export const getRouteType = (pathname: string): string => {
  // Check for dashboard routes first
  if (pathname.includes(ROUTE_PATTERNS.DASHBOARD)) {
    return ROUTE_TYPES.DASHBOARD;
  }
  
  // Check for firm routes with sub-routes
  if (pathname.includes(ROUTE_PATTERNS.FIRMS)) {
    const pathSegments = pathname.split('/').filter(Boolean);
    
    // Check for company pages (firm/companies/...)
    if (pathname.includes(ROUTE_PATTERNS.COMPANIES)) {
      // Find matching company route using company.routes.ts
      const matchingSlug = COMPANY_ROUTE_SLUGS.find(slug =>
        pathname.includes(`/${slug}`)
      );
      
      if (matchingSlug) {
        return matchingSlug; // Return the slug directly as the route type
      }

      // Default company page
      return 'Company';
    }
    
    if (pathname.includes(ROUTE_PATTERNS.FUNDS)) {
      return ROUTE_TYPES.FUND_DETAILS;
    }
    
    // Check for firm-specific pages (direct firm routes)
    if (pathSegments.length === 3 && pathSegments[0] === PATH_SEGMENTS.FIRMS) {
      const pageSegment = pathSegments[2];
      
      // Find matching firm route using firm.routes.ts
      const matchingSlug = FIRM_ROUTE_SLUGS.find(slug => slug === pageSegment);
      
      if (matchingSlug) {
        return matchingSlug; // Return the slug directly as the route type
      }
      
      // Default to summary for unmatched firm pages
      return ROUTE_TYPES.FIRM_SUMMARY;
    }
  }
  
  return ROUTE_TYPES.OVERVIEW;
};

/**
 * Maps route type to page name using company.routes.ts and firm.routes.ts
 */
export const getPageNameFromRouteType = (routeType: string): string => {
  // Check if it's a company route slug
  const companyRouteTitle = getCompanyRouteTitleBySlug(routeType);
  if (companyRouteTitle) {
    return companyRouteTitle;
  }

  // Check if it's a firm route slug  
  const firmRouteTitle = getFirmRouteTitleBySlug(routeType);
  if (firmRouteTitle) {
    return firmRouteTitle;
  }

  // For other routes (fund, dashboard, etc.)
  const otherRouteEntry = Object.entries(ROUTE_TYPES).find(([, value]) => value === routeType);
  if (otherRouteEntry) {
    const [pageName] = otherRouteEntry;
    return PAGE_NAMES[pageName as keyof typeof PAGE_NAMES] || PAGE_NAMES.OVERVIEW;
  }

  return PAGE_NAMES.OVERVIEW;
};

/**
 * Extracts a slug from a specific segment index in the pathname
 */
export const extractSlug = (pathname: string, segmentIndex: number): string => {
  const segments = pathname.split('/').filter(Boolean);
  return segments[segmentIndex] || '';
};

/**
 * Converts kebab-case slugs to readable names, removing ID suffixes
 */
export const slugToReadableName = (slug: string): string => {
  if (!slug){
    return '';
  }
  
  // Remove the ID part (everything after the last dash that's a number)
  const namePart = slug.replace(/-\d+$/, '');
  
  // Convert kebab-case to Title Case
  return namePart
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Extracts and formats measurement dates from URL search parameters
 */
export const extractDateFromUrl = (search: string): string => {
  const urlParams = new URLSearchParams(search);
  const dateParam = urlParams.get(URL_PARAMS.DATE);
  
  if (!dateParam){
    return ''
  }
  
  try {
    // The date format is "2024-12-31-6972" - we need to extract just the date part
    const datePart = dateParam.split('-').slice(0, 3).join('-');

    return dbShortDate(datePart, MDY_FORMAT) || '';
  } catch {
    return '';
  }
};
